import { useState, useEffect, PureComponent } from 'react';
import { useMediaQuery } from '@mui/material';
import {
    LineChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Line,
    XAxis,
    YAxis,
} from 'recharts';
import LinearProgress from '@mui/material/LinearProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import ToggleStyleSwitch from '@CommonRepresentationalDisplay/Switch/ToggleStyleSwitch';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AvTimerRoundedIcon from '@mui/icons-material/AvTimerRounded';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './ProfileStatistics.scss';

export default function ProfileStatistics(props) {
    const [data, setData] = useState([]);
    const [cumulativeData, setCumulativeData] = useState([]);
    const [isCumulative, setIsCumulative] = useState(true);
    const [visibleStatuses, setVisibleStatuses] = useState(['completed', 'assigned', 'overdue', 'ontime']);
    const [timeRange, setTimeRange] = useState(6);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const className = 'profileStatistics';

    useEffect(() => {
        if (!props.userAnalysisLoader && !!props.userAnalysis && Object.keys(props.userAnalysis).length !== 0) {
            parseData();
        }
    }, [props, isCumulative, timeRange]);

    const labelMap = { completed: 'Completed', assigned: 'Assigned', overdue: 'Overdue', ontime: 'On Time' };
    const colorMap = { completed: '#2784B9', assigned: '#807D7D', overdue: '#c31530', ontime: '#7ECA43' };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className={className + "__tooltip"}>
                    <div className={className + "__tooltip__title"}>{label}</div>
                    {payload.map(function(entry, index) {
                        return (
                            <div key={index} className={className + "__tooltip__row"}>
                                <div className={className + "__tooltip__legend"}>
                                    <span
                                        className={className + "__tooltip__dot"}
                                        style={{ backgroundColor: colorMap[entry.dataKey] }}
                                    />
                                    <span className={className + "__tooltip__label"}>{labelMap[entry.dataKey]}</span>
                                </div>
                                <span className={className + "__tooltip__value"} style={{ color: colorMap[entry.dataKey] }}>
                                    {entry.value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    const parseData = () => {
        const dataObj = Array.from({ length: timeRange }, function() {
            return { time: '', assigned: 0, completed: 0, overdue: 0, ontime: 0 };
        });
        const cumulativeObj = [];
        const date = new Date();
        const currMonth = date.getMonth();
        const currYear = date.getFullYear();
        const monthVariableList = ['One', 'Two', 'Three', 'Four', 'Five', 'Six'];
        const selectedMonthVars = monthVariableList.slice(0, timeRange).reverse();
        let totalAssigned = 0, totalCompleted = 0, totalOverdue = 0, totalOntime = 0;
        for (let i = 0; i < dataObj.length; i++) {
            let currentDate = new Date(currYear, currMonth - (dataObj.length - i - 1), 1);
            const dateParts = currentDate.toString().split(' ');
            const monthKey = 'userMonth' + selectedMonthVars[i];
            const monthData = props.userAnalysis[monthKey] || {};
            const timeLabel = dateParts[1] + ' ' + dateParts[3].substring(2);
            dataObj[i] = {
                time: timeLabel,
                completed: monthData['completed'] || 0,
                assigned: monthData['assigned'] || 0,
                overdue: monthData['overdue'] || 0,
                ontime: monthData['ontime'] || 0
            };
            totalCompleted += monthData['completed'] || 0;
            totalAssigned += monthData['assigned'] || 0;
            totalOverdue += monthData['overdue'] || 0;
            totalOntime += monthData['ontime'] || 0;
            cumulativeObj[i] = {
                time: timeLabel,
                completed: totalCompleted,
                assigned: totalAssigned,
                overdue: totalOverdue,
                ontime: totalOntime
            };
        }
        setCumulativeData(cumulativeObj);
        setData(isCumulative ? cumulativeObj : dataObj);
    };

    const toggleStatus = function(status) {
        setVisibleStatuses(function(prev) {
            return prev.includes(status)
                ? prev.filter(function(s) { return s !== status; })
                : prev.concat(status);
        });
    };

    const latestTotals = cumulativeData.length > 0
        ? cumulativeData[cumulativeData.length - 1]
        : { completed: 0, assigned: 0, overdue: 0, ontime: 0 };

    return (
        <div className={className}>
            {/* Header */}
            <div className={className + '__header'}>
                <div className={className + '__header__left'}>
                    <div className={className + '__title'}>Performance Trends</div>
                    {!isMobile && <div className={className + '__subtitle'}>Task completion over time</div>}
                </div>
                <div className={className + '__header__right'}>
                    <div className={className + '__control'}>
                        {!isMobile && <label htmlFor="time-range-select" className={className + '__control__label'}>Range</label>}
                        <Select
                            id="time-range-select"
                            value={timeRange}
                            onChange={function(e) { setTimeRange(parseInt(e.target.value, 10)); }}
                            className={className + '__select'}
                            size="small">
                            <MenuItem value={6}>Last 6 Months</MenuItem>
                            <MenuItem value={3}>Last 3 Months</MenuItem>
                        </Select>
                    </div>
                    <div className={className + '__control ' + className + '__control--switch'}>
                        <ToggleStyleSwitch
                            checked={isCumulative}
                            onChange={(e) => setIsCumulative(e.target.checked)}
                        />
                        {!isMobile && <div className={className + '__switchLabel'}>Cumulative</div>}
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            {props.userAnalysisLoader ? (
                <div className={className + '__kpis ' + className + '__kpis--loading'}>
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className={className + '__kpi'}>
                            <Skeleton variant="circular" width={isMobile ? 48 : 36} height={isMobile ? 48 : 36} />
                            <div className={className + '__kpi__content'}>
                                <Skeleton width={90} height={isMobile ? 16 : 18} />
                                <Skeleton width={60} height={isMobile ? 24 : 22} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={className + '__kpis'}>
                    <div
                        className={className + '__kpi'}
                        onClick={() => toggleStatus('completed')}
                        style={{ 
                            opacity: visibleStatuses.includes('completed') ? 1 : 0.5, 
                            borderColor: visibleStatuses.includes('completed') ? 'var(--color-primaryBlue)' : 'var(--color-darkBorder)' 
                        }}>
                        <div className={className + '__kpi__icon kpi--blue'}>
                            <CheckCircleOutlineRoundedIcon />
                        </div>
                        <div className={className + '__kpi__content'}>
                            <div className={className + '__kpi__label'}>Completed</div>
                            <div className={className + '__kpi__value'}>{latestTotals.completed}</div>
                        </div>
                    </div>
                    <div
                        className={className + '__kpi'}
                        onClick={() => toggleStatus('assigned')}
                        style={{ 
                            opacity: visibleStatuses.includes('assigned') ? 1 : 0.5, 
                            borderColor: visibleStatuses.includes('assigned') ? 'var(--color-primaryGrey)' : 'var(--color-darkBorder)' 
                        }}>
                        <div className={className + '__kpi__icon kpi--grey'}>
                            <AssignmentOutlinedIcon />
                        </div>
                        <div className={className + '__kpi__content'}>
                            <div className={className + '__kpi__label'}>Assigned</div>
                            <div className={className + '__kpi__value'}>{latestTotals.assigned}</div>
                        </div>
                    </div>
                    <div
                        className={className + '__kpi'}
                        onClick={() => toggleStatus('ontime')}
                        style={{ 
                            opacity: visibleStatuses.includes('ontime') ? 1 : 0.5, 
                            borderColor: visibleStatuses.includes('ontime') ? 'var(--color-primaryGreen)' : 'var(--color-darkBorder)' 
                        }}>
                        <div className={className + '__kpi__icon kpi--green'}>
                            <ChecklistRoundedIcon />
                        </div>
                        <div className={className + '__kpi__content'}>
                            <div className={className + '__kpi__label'}>On Time</div>
                            <div className={className + '__kpi__value'}>{latestTotals.ontime}</div>
                        </div>
                    </div>
                    <div
                        className={className + '__kpi'}
                        onClick={() => toggleStatus('overdue')}
                        style={{ 
                            opacity: visibleStatuses.includes('overdue') ? 1 : 0.5, 
                            borderColor: visibleStatuses.includes('overdue') ? 'var(--color-primaryRed)' : 'var(--color-darkBorder)' 
                        }}>
                        <div className={className + '__kpi__icon kpi--red'}>
                            <AvTimerRoundedIcon />
                        </div>
                        <div className={className + '__kpi__content'}>
                            <div className={className + '__kpi__label'}>Overdue</div>
                            <div className={className + '__kpi__value'}>{latestTotals.overdue}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Chart Card */}
            <div className={className + '__card'}>
                {props.userAnalysisLoader ? (
                    <div className={className + '__loader'}>
                        <LinearProgress className={className + '__loader__bar default-loader'} color="inherit" />
                        <LinearProgress className={className + '__loader__bar default-loader'} color="inherit" />
                        <LinearProgress className={className + '__loader__bar default-loader'} color="inherit" />
                    </div>
                ) : (
                    <div className={className + '__chart'}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data} margin={{ top: 10, right: isMobile ? 8 : 16, left: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="time"
                                    tick={{ fill: 'var(--color-primaryText)', fontSize: isMobile ? 11 : 12 }}
                                    axisLine={{ stroke: 'var(--color-primaryText)' }}
                                    tickLine={{ stroke: 'var(--color-primaryText)' }}
                                />
                                <YAxis
                                    tick={{ fill: 'var(--color-primaryText)', fontSize: isMobile ? 11 : 12 }}
                                    axisLine={{ stroke: 'var(--color-primaryText)' }}
                                    tickLine={{ stroke: 'var(--color-primaryText)' }}
                                    tickCount={4}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                {visibleStatuses.includes('completed') && (
                                    <Line
                                        type="monotone"
                                        dataKey="completed"
                                        stroke={colorMap.completed}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        strokeWidth={2.5}
                                    />
                                )}
                                {visibleStatuses.includes('assigned') && (
                                    <Line
                                        type="monotone"
                                        dataKey="assigned"
                                        stroke={colorMap.assigned}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        strokeWidth={2.5}
                                    />
                                )}
                                {visibleStatuses.includes('overdue') && (
                                    <Line
                                        type="monotone"
                                        dataKey="overdue"
                                        stroke={colorMap.overdue}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        strokeWidth={2.5}
                                    />
                                )}
                                {visibleStatuses.includes('ontime') && (
                                    <Line
                                        type="monotone"
                                        dataKey="ontime"
                                        stroke={colorMap.ontime}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        strokeWidth={2.5}
                                    />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
